import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import {
  BeachAccess as VacationIcon,
  LocalHospital as SickIcon,
  EventNote as PersonalIcon,
} from '@mui/icons-material';
import { LeaveBalance } from '../../types';

interface LeaveBalanceCardProps {
  balance: LeaveBalance;
}

const LeaveBalanceCard: React.FC<LeaveBalanceCardProps> = ({ balance }) => {
  const vacationRemaining = balance.vacationDaysTotal - balance.vacationDaysUsed;
  const sickRemaining = balance.sickDaysTotal - balance.sickDaysUsed;
  const personalRemaining = balance.personalDaysTotal - balance.personalDaysUsed;
  
  const vacationProgress = (balance.vacationDaysUsed / balance.vacationDaysTotal) * 100;
  const sickProgress = (balance.sickDaysUsed / balance.sickDaysTotal) * 100;
  const personalProgress = (balance.personalDaysUsed / balance.personalDaysTotal) * 100;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          Leave Balance for {balance.year}
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Vacation Days */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <VacationIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Vacation Days
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Used: {balance.vacationDaysUsed} / {balance.vacationDaysTotal}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary">
                  {vacationRemaining} remaining
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={vacationProgress}
                color="primary"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>

          {/* Sick Days */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SickIcon color="error" />
              <Typography variant="subtitle1" fontWeight={600}>
                Sick Days
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Used: {balance.sickDaysUsed} / {balance.sickDaysTotal}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="error">
                  {sickRemaining} remaining
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={sickProgress}
                color="error"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>

          {/* Personal Days */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonalIcon sx={{ color: '#4DB8AC' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                Personal Days
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Used: {balance.personalDaysUsed} / {balance.personalDaysTotal}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: '#4DB8AC' }}>
                  {personalRemaining} remaining
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={personalProgress}
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'rgba(77, 184, 172, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4DB8AC'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Summary */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Total days remaining: <strong>{vacationRemaining + sickRemaining + personalRemaining} days</strong>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LeaveBalanceCard; 